import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CATEGORIES, COUNTRIES, FEATURED_HERO_CATEGORIES, categoryBadgeClass, categoryAccent } from "@/lib/categories";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MapPin, ArrowRight, Sparkles, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function HomePage() {
  const navigate = useNavigate();
  const [category, setCategory] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("España");

  const onSearch = (e) => {
    e.preventDefault();
    if (!category) {
      toast.error("Elige una sección para continuar");
      return;
    }
    if (!city.trim()) {
      toast.error("Escribe tu ciudad");
      return;
    }
    const params = new URLSearchParams({
      categoria: category,
      ciudad: city.trim(),
      pais: country,
    });
    navigate(`/anuncios?${params.toString()}`);
  };

  return (
    <div className="bg-[#F8FAFC]">
      {/* Hero — editorial split, brand-blue palette, high legibility */}
      <section className="relative overflow-hidden bg-[#0A1930] text-[#F8FAFC]">
        {/* subtle grain + radial glow for depth */}
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          aria-hidden="true"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='300' height='300'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          }}
        />
        <div
          className="absolute -top-40 -right-32 w-[560px] h-[560px] rounded-full pointer-events-none"
          aria-hidden="true"
          style={{
            background:
              "radial-gradient(circle at center, rgba(59,95,184,0.35) 0%, rgba(30,58,138,0.15) 40%, transparent 70%)",
          }}
        />
        <div
          className="absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full pointer-events-none"
          aria-hidden="true"
          style={{
            background:
              "radial-gradient(circle at center, rgba(148,163,184,0.20) 0%, transparent 70%)",
          }}
        />
        {/* fine top hairline */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#94A3B8]/40 to-transparent" aria-hidden="true" />

        <div className="relative max-w-5xl mx-auto w-full px-5 sm:px-8 py-20 sm:py-24 lg:py-32">
          {/* CENTERED content */}
          <div className="flex flex-col items-center text-center space-y-8 fade-up">
            <span
              className="inline-flex items-center gap-2 border border-[#94A3B8]/40 bg-[#152B54]/60 backdrop-blur-sm px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.22em] text-[#CBD5E1]"
              data-testid="hero-trust-badge"
            >
              <ShieldAlert className="w-3 h-3 text-[#94B8DA]" />
              Próximamente · Fase 1 · Compartir piso
            </span>

            <h1
              data-testid="hero-title"
              className="font-serif text-[38px] sm:text-6xl lg:text-[80px] leading-[1.02] font-semibold tracking-tight max-w-4xl"
            >
              Empezaremos muy{" "}
              <em className="not-italic italic text-[#94B8DA]">pronto</em>{" "}
              compartiendo piso.
              <br className="hidden sm:block" />
              <span className="text-[#F8FAFC]"> Seguiremos creciendo como comunidad.</span>
            </h1>

            <p className="text-sm sm:text-lg leading-relaxed max-w-xl text-[#CBD5E1] px-2 sm:px-0">
              Buscamos vivir mejor <strong className="text-[#F8FAFC] font-semibold">compartiendo</strong>, vivir mejor <strong className="text-[#F8FAFC] font-semibold">ahorrando</strong>. Arrancaremos con habitaciones y viviendas compartidas — el resto de secciones llegará muy pronto.
            </p>

            {/* Search form — clean white card, centered */}
            <form
              onSubmit={onSearch}
              data-testid="portada-search-form"
              className="w-full max-w-3xl bg-white border border-[#E2E8F0] shadow-[0_25px_60px_-15px_rgba(10,25,48,0.5)] p-5 sm:p-6 grid gap-4 sm:grid-cols-12 text-left"
            >
              <div className="sm:col-span-4 space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#1E3A8A]">
                  Sección
                </Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger
                    data-testid="portada-category-select"
                    className="rounded-none h-12 border-[#CBD5E1] bg-white text-[#0A1930] font-medium focus:border-[#1E3A8A]"
                  >
                    <SelectValue placeholder="Elige sección" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {CATEGORIES.map((c) => (
                      <SelectItem
                        key={c.slug}
                        value={c.slug}
                        data-testid={`portada-category-${c.slug}`}
                        disabled={!c.active}
                        className={!c.active ? "opacity-50" : ""}
                      >
                        {c.label}{!c.active && " · Próximamente"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-4 space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#1E3A8A]">
                  Ciudad
                </Label>
                <div className="relative">
                  <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#3B5FB8] pointer-events-none" />
                  <Input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    data-testid="portada-city-input"
                    placeholder="p. ej. Madrid"
                    className="rounded-none h-12 pl-9 border-[#CBD5E1] bg-white font-medium focus:border-[#1E3A8A]"
                  />
                </div>
              </div>
              <div className="sm:col-span-4 space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#1E3A8A]">
                  País
                </Label>
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger
                    data-testid="portada-country-select"
                    className="rounded-none h-12 border-[#CBD5E1] bg-white font-medium focus:border-[#1E3A8A]"
                  >
                    <SelectValue placeholder="Elige país" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none max-h-72">
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-12 flex justify-center">
                <Button
                  type="submit"
                  data-testid="portada-search-submit"
                  className="w-full sm:w-auto h-12 px-10 bg-[#1E3A8A] hover:bg-[#152B6B] text-[#F8FAFC] rounded-none font-medium tracking-wide shadow-lg shadow-[#1E3A8A]/20"
                >
                  Ver anuncios locales
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </form>

            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-[#94A3B8]">
              <span className="flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-[#94B8DA]" />
                Publicar es gratis
              </span>
              <span className="text-[#475569]">·</span>
              <span>Sin comisiones</span>
              <span className="text-[#475569]">·</span>
              <span>Contacto directo</span>
            </div>

            {/* Secciones destacadas — horizontal row, centered */}
            <div className="w-full pt-8 mt-4 border-t border-[#94A3B8]/15 fade-up-delay-2">
              <div className="flex items-baseline justify-center gap-3 mb-5">
                <span className="h-px w-8 bg-[#94A3B8]/30" aria-hidden="true" />
                <p className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#94B8DA]">
                  Fase 1 · Compartir vivienda
                </p>
                <span className="h-px w-8 bg-[#94A3B8]/30" aria-hidden="true" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {CATEGORIES.filter((c) => FEATURED_HERO_CATEGORIES.includes(c.slug)).map((c, idx) => (
                  <button
                    key={c.slug}
                    onClick={() => {
                      if (!c.active) return;
                      setCategory(c.slug);
                      document.querySelector('[data-testid="portada-city-input"]')?.focus();
                    }}
                    disabled={!c.active}
                    aria-disabled={!c.active}
                    data-testid={`hero-tile-${c.slug}`}
                    className={`group relative overflow-hidden border transition-all duration-300 flex flex-col items-center justify-center py-5 px-3 ${
                      !c.active
                        ? "border-[#94A3B8]/15 bg-[#0F2547]/30 cursor-not-allowed"
                        : category === c.slug
                        ? "border-[#94B8DA] bg-[#152B54]"
                        : "border-[#94A3B8]/25 bg-[#0F2547]/60 hover:bg-[#152B54] hover:border-[#94A3B8]/50"
                    } backdrop-blur-sm`}
                  >
                    <span className={`font-serif text-2xl italic font-semibold mb-1 ${c.active ? "text-[#94B8DA]" : "text-[#64748B]"}`}>
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <p className={`font-serif text-lg font-semibold leading-tight ${c.active ? "text-[#F8FAFC]" : "text-[#94A3B8]"}`}>
                      {c.label}
                    </p>
                    {c.active ? (
                      <ArrowRight className="w-3.5 h-3.5 mt-1.5 text-[#94B8DA] opacity-40 group-hover:opacity-100 group-hover:translate-y-0.5 transition-all" />
                    ) : (
                      <span className="text-[9px] font-mono uppercase tracking-[0.18em] text-[#64748B] mt-1.5">Próximamente</span>
                    )}
                  </button>
                ))}
              </div>
              <button
                onClick={() => {
                  document.getElementById("todas-categorias")?.scrollIntoView({ behavior: "smooth" });
                }}
                data-testid="hero-see-all-categories"
                className="mt-5 mx-auto flex items-center gap-2 h-11 px-4 text-xs font-mono uppercase tracking-widest text-[#94B8DA] hover:text-[#F8FAFC] hover:bg-[#152B54]/40 transition-colors border border-transparent hover:border-[#94A3B8]/25"
              >
                Ver las 8 categorías ↓
              </button>
            </div>
          </div>
        </div>
        {/* bottom fade to page bg */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-b from-transparent to-[#F8FAFC]/5 pointer-events-none" aria-hidden="true" />
      </section>

      {/* Realistic facts band */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#1E3A8A] via-[#3B5FB8] to-[#4A5568]" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[#F8FAFC]">
          <div className="flex items-baseline gap-3">
            <span className="font-serif text-4xl lg:text-5xl font-semibold italic">1/8</span>
            <span className="font-mono uppercase tracking-[0.18em] text-xs">sección activa · Pisos</span>
          </div>
          <div className="flex items-baseline gap-3">
            <span className="font-serif text-4xl lg:text-5xl font-semibold italic">0 €</span>
            <span className="font-mono uppercase tracking-[0.18em] text-xs">publicar es gratis</span>
          </div>
          <div className="flex items-baseline gap-3">
            <span className="font-serif text-3xl lg:text-4xl font-semibold italic">Sin</span>
            <span className="font-mono uppercase tracking-[0.18em] text-xs">comisiones</span>
          </div>
          <div className="flex items-baseline gap-3">
            <span className="font-serif text-3xl lg:text-4xl font-semibold italic">Contacto</span>
            <span className="font-mono uppercase tracking-[0.18em] text-xs">directo con el anunciante</span>
          </div>
        </div>
      </section>

      {/* Security notice band */}
      <section className="relative overflow-hidden bg-[#EEF4FB] border-t border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 flex items-center justify-center bg-white border border-[#A3B8DA]">
              <ShieldAlert className="w-5 h-5 text-[#1E3A8A]" />
            </div>
            <p className="eyebrow" style={{ color: "#1E3A8A" }}>Aviso de seguridad</p>
          </div>
          <p className="text-sm text-[#475569] leading-relaxed md:flex-1">
            <strong className="text-[#1A1A1A]">Nunca compartas datos bancarios ni realices pagos por esta plataforma.</strong>{" "}
            buscocompartir no se hace responsable de posibles fraudes cometidos por anunciantes. Consulta el{" "}
            <Link to="/aviso-legal" className="text-[#1E3A8A] font-semibold link-underline">
              aviso legal
            </Link>{" "}
            para más información.
          </p>
        </div>
      </section>

      {/* Category detail cards */}
      <section id="todas-categorias" className="border-t border-[#E2E8F0] section-sage relative overflow-hidden">
        <div className="absolute -top-32 -left-20 w-96 h-96 rounded-full bg-[#3B5FB8] opacity-[0.05] blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-32 -right-20 w-96 h-96 rounded-full bg-[#1E3A8A] opacity-[0.06] blur-3xl" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24 relative">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-10">
            <div>
              <p className="eyebrow mb-2">Empezaremos por Pisos · El resto muy pronto</p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1A1A1A]">
                Ocho <span className="accent-underline">secciones</span>, una comunidad
              </h2>
            </div>
            <p className="text-sm text-[#475569] max-w-md">
              Arrancaremos con habitaciones y viviendas para compartir. Las otras 7 secciones se irán abriendo a medida que crezca la comunidad.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {CATEGORIES.map((c) => (
              <button
                key={c.slug}
                data-testid={`section-card-${c.slug}`}
                onClick={() => c.active && navigate(`/anuncios?categoria=${c.slug}`)}
                disabled={!c.active}
                aria-disabled={!c.active}
                className={`group relative overflow-hidden card-elevate bg-white border border-[#E2E8F0] aspect-[4/3] text-left flex flex-col ${
                  c.active ? "" : "cursor-not-allowed"
                }`}
              >
                <div className="relative flex-1 overflow-hidden">
                  <img
                    src={c.image}
                    alt={c.label}
                    loading="lazy"
                    className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 ${
                      c.active ? "group-hover:scale-[1.08]" : "grayscale opacity-60"
                    }`}
                  />
                  {/* Category-tinted colour wash */}
                  <div
                    className={`absolute inset-0 mix-blend-multiply transition-opacity duration-500 ${
                      c.active ? "opacity-45 group-hover:opacity-30" : "opacity-70"
                    }`}
                    style={{ backgroundColor: c.active ? c.accent : "#475569" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/40 via-transparent to-transparent" />
                  <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                    <span
                      className={`inline-flex px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em] border ${categoryBadgeClass(
                        c.slug,
                      )} bg-white/90`}
                    >
                      {c.label}
                    </span>
                    {c.active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em] bg-[#1E3A8A] text-[#F8FAFC]">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="absolute inline-flex h-full w-full rounded-full bg-[#94B8DA] opacity-75 animate-ping"></span>
                          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#94B8DA]"></span>
                        </span>
                        Activo
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em] bg-[#0A1930]/80 text-[#CBD5E1] backdrop-blur-sm">
                        Próximamente
                      </span>
                    )}
                  </div>
                </div>
                <div
                  className="text-[#F8FAFC] px-4 py-3 flex items-center justify-between"
                  style={{ backgroundColor: c.active ? c.accent : "#475569" }}
                >
                  <p className="font-serif text-base lg:text-lg font-medium leading-snug">
                    {c.tagline}
                  </p>
                  {c.active ? (
                    <ArrowRight className="w-4 h-4 opacity-80 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                  ) : (
                    <span className="text-[10px] font-mono uppercase tracking-widest opacity-80">Muy pronto</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial statement — asymmetric colour block */}
      <section className="section-warm border-t border-b border-[#E2E8F0] relative overflow-hidden">
        <div className="absolute inset-0 opacity-40 pointer-events-none" aria-hidden="true">
          <div className="absolute top-10 left-1/4 w-64 h-64 rounded-full bg-[#1E3A8A] opacity-20 blur-3xl" />
          <div className="absolute bottom-10 right-1/4 w-72 h-72 rounded-full bg-[#4A5568] opacity-20 blur-3xl" />
        </div>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 lg:py-28 relative grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-2 hidden lg:block">
            <p className="font-serif text-[140px] leading-none text-[#3B5FB8] italic">"</p>
          </div>
          <div className="lg:col-span-8 space-y-4">
            <p className="eyebrow" style={{ color: "#1E3A8A" }}>Manifiesto</p>
            <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1A1A1A] leading-[1.1]">
              Creemos en compartir <em className="text-[#3B5FB8] not-italic italic">sin barreras</em>,
              en anuncios que se leen sin prisa y en una comunidad que se respeta.
            </h3>
            <p className="text-lg text-[#475569] leading-relaxed max-w-2xl">
              Nada de scroll infinito, ni presión por comprar. Sólo un tablón claro y directo donde
              vecinos, estudiantes y empresas se encuentran para compartir lo que a otros les puede
              venir bien.
            </p>
          </div>
          <div className="lg:col-span-2 hidden lg:flex items-end justify-end">
            <p className="font-serif text-[140px] leading-none text-[#3B5FB8] italic rotate-180">"</p>
          </div>
        </div>
      </section>

      {/* Trust / how it works */}
      <section className="border-t border-[#E2E8F0] section-ink relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-30" aria-hidden="true">
          <div className="absolute top-10 left-10 w-80 h-80 rounded-full bg-[#1E3A8A] opacity-40 blur-3xl" />
          <div className="absolute bottom-10 right-20 w-96 h-96 rounded-full bg-[#4A5568] opacity-40 blur-3xl" />
          <div className="absolute top-1/2 right-1/2 w-64 h-64 rounded-full bg-[#3B5FB8] opacity-40 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24 relative">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-12">
            <div>
              <p className="eyebrow" style={{ color: "#94A3B8" }}>Cómo funciona</p>
              <h3 className="mt-2 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#F8FAFC]">
                Tres pasos y ya está.
              </h3>
            </div>
          </div>
          <div className="grid gap-8 lg:grid-cols-3">
          {[
            {
              n: "01",
              t: "Elige tu sección y ciudad",
              d: "Selecciona la categoría que buscas y tu ciudad para ver únicamente anuncios locales relevantes.",
              c: "#94A3B8",
              tag: "Buscar",
            },
            {
              n: "02",
              t: "Descubre y contacta",
              d: "Explora anuncios con fotos, precio y ubicación. Contacta directamente por email o teléfono con el anunciante.",
              c: "#E29D7C",
              tag: "Contactar",
            },
            {
              n: "03",
              t: "Publica en minutos",
              d: "Crea tu anuncio con hasta 5 imágenes. Sin comisiones, sin intermediarios. Elegante y directo.",
              c: "#B9C99A",
              tag: "Publicar",
            },
          ].map((step) => (
            <div key={step.n} className="relative border-t-2 pt-6" style={{ borderColor: step.c }}>
              <div className="flex items-baseline justify-between mb-3">
                <span className="font-serif text-[80px] leading-none italic" style={{ color: step.c }}>
                  {step.n}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: step.c }}>
                  {step.tag}
                </span>
              </div>
              <h4 className="font-serif text-2xl font-semibold mb-2 text-[#F8FAFC]">{step.t}</h4>
              <p className="text-[#CBD5E1] leading-relaxed text-sm">{step.d}</p>
            </div>
          ))}
          </div>
        </div>
      </section>
    </div>
  );
}
